import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import DataColumnMenu from './DataColumnMenu';
import {
  applyColumnFilters,
  applySort,
  columnHasActiveFilter,
  groupRows,
} from './columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from './groupCollapse';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

export const defaultColumnVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

/**
 * Client-side list grid state (sort / filter / group / page).
 * `getCellValue(row, field)` should return the display/sort/filter value.
 */
export function useClientListGrid({ columns, rows, getCellValue, initialPageSize = 20 }) {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultColumnVisibility(columns));
  const [groupField, setGroupField] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = defaultColumnVisibility(columns);
      columns.forEach((col) => {
        if (prev[col.field] !== undefined) next[col.field] = prev[col.field];
      });
      return next;
    });
  }, [columns]);

  const mappedRows = useMemo(
    () =>
      (rows || []).map((row) => {
        const mapped = { ...row };
        columns.forEach((col) => {
          if (col.field === 'actions') return;
          mapped[col.field] = getCellValue ? getCellValue(row, col.field) : row[col.field];
        });
        return mapped;
      }),
    [rows, columns, getCellValue]
  );

  const processedRows = useMemo(() => {
    let next = applyColumnFilters(mappedRows, filtersByField);
    next = applySort(next, sortField, sortDirection);
    return next;
  }, [mappedRows, filtersByField, sortField, sortDirection]);

  const totalCount = processedRows.length;
  const effectivePageSize = pageSize === 'all' || groupField ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' || groupField ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    if (groupField || pageSize === 'all') return processedRows;
    const size = Number(pageSize);
    const start = (safePage - 1) * size;
    return processedRows.slice(start, start + size);
  }, [processedRows, groupField, pageSize, safePage]);

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: pagedRows }];
    return groupRows(processedRows, groupField);
  }, [pagedRows, processedRows, groupField]);

  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const colSpan = useMemo(
    () => columns.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columns, columnVisibility]
  );

  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' || groupField ? 1 : (safePage - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' || groupField ? totalCount : Math.min(safePage * Number(pageSize), totalCount));
  const pageItems = useMemo(() => buildPageItems(safePage, totalPages), [safePage, totalPages]);

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = columns.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    setFiltersByField((prev) => ({ ...prev, [field]: filter }));
    setPage(1);
  };

  const handleClearColumnFilter = (field) => {
    setFiltersByField((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setPage(1);
  };

  const handleGroupBy = (field) => {
    setGroupField(field);
    setPage(1);
    setPageSize('all');
  };

  const handleUngroup = () => {
    setGroupField(null);
    setPage(1);
    if (pageSize === 'all') setPageSize(initialPageSize);
  };

  const clearSortAndFilters = () => {
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    setPage(1);
    if (pageSize === 'all') setPageSize(initialPageSize);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value === 'all' ? 'all' : Number(value));
    setPage(1);
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === safePage) return;
    setPage(nextPage);
  };

  return {
    sortField,
    sortDirection,
    filtersByField,
    columnVisibility,
    groupField,
    page: safePage,
    pageSize,
    displayGroups,
    processedRows,
    totalCount,
    totalPages,
    colSpan,
    activeFilterCount,
    rangeFrom,
    rangeTo,
    pageItems,
    handleColumnSort,
    handleToggleColumn,
    handleApplyColumnFilter,
    handleClearColumnFilter,
    handleGroupBy,
    handleUngroup,
    clearSortAndFilters,
    handlePageSizeChange,
    goToPage,
  };
}

export function exportClientListExcel({
  sheetName,
  fileName,
  columns,
  groups,
  groupField,
  getCellValue,
}) {
  const dataCols = columns.filter((c) => c.field !== 'actions');
  exportGroupedExcel({
    sheetName,
    fileName,
    headers: ['#', ...dataCols.map((c) => c.title)],
    groups,
    groupFieldTitle: columns.find((c) => c.field === groupField)?.title || 'Group',
    mapRow: (row, serial) => [
      serial,
      ...dataCols.map((c) => (getCellValue ? getCellValue(row, c.field) : row[c.field])),
    ],
  });
}

/**
 * Shared list table chrome matching Accounts/Orders grid UX.
 */
const ClientListGrid = ({
  columns,
  grid,
  renderCell,
  loading = false,
  loadingText = 'Loading...',
  emptyText = 'No records found',
  minWidth = '1100px',
  rowKey,
  maxHeight = 560,
  showPagination = true,
  leadingHeader = null,
  renderLeadingCell = null,
  extraColSpan = 0,
  tableClassName = 'data-grid-table w-full',
  theadClassName = 'data-grid-thead sticky top-0 z-30',
  serialHeader = '#',
  footerExtra = null,
  onRowClick = null,
  onRowContextMenu = null,
  getRowClassName = null,
  getCellClassName = null,
  compact = false,
}) => {
  const {
    sortField,
    sortDirection,
    filtersByField,
    columnVisibility,
    groupField,
    page,
    pageSize,
    displayGroups,
    totalCount,
    totalPages,
    colSpan,
    activeFilterCount,
    rangeFrom,
    rangeTo,
    pageItems,
    handleColumnSort,
    handleToggleColumn,
    handleApplyColumnFilter,
    handleClearColumnFilter,
    handleGroupBy,
    handleUngroup,
    clearSortAndFilters,
    handlePageSizeChange,
    goToPage,
  } = grid;

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);
  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );
  const fullColSpan = colSpan + extraColSpan + (leadingHeader ? 1 : 0);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight }}>
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-gray-600">{loadingText}</span>
            </div>
          </div>
        )}

        {(activeFilterCount > 0 || sortField) && (
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
            <span>
              {sortField
                ? `Sorted by ${columns.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                : 'Filters active'}
              {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
            </span>
            <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
              Clear sort & filters
            </button>
          </div>
        )}

        {groupField && (
          <GroupByBanner
            groupTitle={columns.find((c) => c.field === groupField)?.title || groupField}
            groupKeys={groupKeys}
            onUngroup={handleUngroup}
            onCollapseAll={collapseAll}
            onExpandAll={expandAll}
          />
        )}

        <table className={tableClassName} style={{ minWidth }}>
          <thead className={theadClassName}>
            <tr>
              {leadingHeader}
              <th className="data-grid-th data-grid-th-serial w-12 min-w-[3rem] px-3 py-2.5 text-left">
                {serialHeader}
              </th>
              {columns.map((column) =>
                columnVisibility[column.field] === false ? null : (
                  <DataColumnMenu
                    key={column.field}
                    column={column}
                    columns={columns}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleColumnSort}
                    columnVisibility={columnVisibility}
                    onToggleColumn={handleToggleColumn}
                    filtersByField={filtersByField}
                    onApplyFilter={handleApplyColumnFilter}
                    onClearFilter={handleClearColumnFilter}
                    groupField={groupField}
                    onGroupBy={handleGroupBy}
                    onUngroup={handleUngroup}
                    align={column.align}
                  />
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {totalCount === 0 ? (
              <tr>
                <td colSpan={fullColSpan} className="px-6 py-12 text-center text-gray-500">
                  {emptyText}
                </td>
              </tr>
            ) : (
              (() => {
                let serial = 0;
                return displayGroups.flatMap((group) => {
                  const block = [];
                  if (group.label != null) {
                    block.push(
                      <tr key={`group-${group.key}`} className="bg-gray-100">
                        <td colSpan={fullColSpan} className="px-4 py-2">
                          <GroupHeaderLabel
                            title={columns.find((c) => c.field === groupField)?.title}
                            label={group.label}
                            count={group.rows.length}
                            collapsed={isCollapsed(group.key)}
                            onToggle={() => toggleGroup(group.key)}
                          />
                        </td>
                      </tr>
                    );
                  }
                  if (group.label != null && isCollapsed(group.key)) {
                    return block;
                  }
                  group.rows.forEach((row, index) => {
                    serial += 1;
                    const key = rowKey ? rowKey(row, index) : row.Id || row.id || `${group.key}-${index}`;
                    const serialNo = groupField || pageSize === 'all' ? serial : rangeFrom + serial - 1;
                    const extraClass = getRowClassName ? getRowClassName(row) : '';
                    block.push(
                      <tr
                        key={key}
                        data-row-id={row.Id ?? row.id ?? undefined}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        onContextMenu={onRowContextMenu ? (e) => onRowContextMenu(e, row) : undefined}
                        className={`transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${extraClass || 'hover:bg-gray-50'}`}
                      >
                        {renderLeadingCell ? renderLeadingCell(row) : null}
                        <td className={`table-cell-serial px-4 py-2 text-gray-600 ${compact ? 'text-xs' : 'text-sm'}`}>{serialNo}</td>
                        {columns.map((column) =>
                          columnVisibility[column.field] === false ? null : (
                            <td
                              key={column.field}
                              className={`px-4 py-2 text-gray-600 ${compact ? 'text-xs' : 'text-sm'} ${
                                column.field === 'actions' ? 'table-cell-actions' : ''
                              } ${
                                column.align === 'center' ? 'text-center' : ''
                              } ${column.align === 'right' ? 'text-right' : ''} ${
                                getCellClassName ? getCellClassName(row, column) : ''
                              }`}
                            >
                              {renderCell(row, column)}
                            </td>
                          )
                        )}
                      </tr>
                    );
                  });
                  return block;
                });
              })()
            )}
            {footerExtra}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0
                  ? emptyText
                  : `Showing ${rangeFrom} to ${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField ? ` · grouped by ${columns.find((c) => c.field === groupField)?.title || groupField}` : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientListGrid;
